"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { PhoneFrame } from "@/components/phone-frame";
import { useSectionViewEvent } from "@/hooks/use-section-view-event";
import { PI_SECTION } from "@/lib/analytics/schema";
import { featureCards } from "@/lib/feature-cards-data";

const SECTION_TITLE = "Everything you need to study, in one place";

export function FeaturesGridSection() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useSectionViewEvent({
    sectionId: "features",
    sectionName: PI_SECTION.FEATURES.name,
    sectionPosition: PI_SECTION.FEATURES.position,
  });

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const handleScroll = () => {
      const cards = Array.from(scroller.children) as HTMLElement[];
      const center = scroller.scrollLeft + scroller.clientWidth / 2;
      let nearest = 0;
      let nearestDistance = Infinity;

      cards.forEach((card, index) => {
        const distance = Math.abs(
          card.offsetLeft + card.offsetWidth / 2 - center,
        );
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = index;
        }
      });

      setActiveIndex(nearest);
    };

    scroller.addEventListener("scroll", handleScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToIndex = (index: number) => {
    const scroller = scrollerRef.current;
    const card = scroller?.children[index] as HTMLElement | undefined;
    if (!scroller || !card) return;

    scroller.scrollTo({
      left: card.offsetLeft - (scroller.clientWidth - card.offsetWidth) / 2,
      behavior: "smooth",
    });
  };

  return (
    <section id="features" className="bg-pi-bg">
      <div className="mx-auto max-w-[1200px] px-4 py-14 pb-6 sm:px-6 md:py-20 md:pb-10 lg:px-8 lg:py-24">
        <h2 className="m-0 text-[clamp(26px,4vw,34px)] font-semibold leading-tight text-pi-bright">
          {SECTION_TITLE}
        </h2>
        <p className="mt-3.5 mb-0 text-[clamp(15px,1.5vw,18px)] leading-relaxed text-pi-text">
          No more juggling YouTube tabs, PDFs and Telegram groups. Every type of
          self-study content, under one subscription.
        </p>

        <div
          ref={scrollerRef}
          role="group"
          aria-roledescription="carousel"
          aria-label={SECTION_TITLE}
          className="mt-8 grid grid-cols-1 gap-[18px] max-sm:-mx-4 max-sm:mt-6 max-sm:flex max-sm:snap-x max-sm:snap-mandatory max-sm:items-start max-sm:overflow-x-auto max-sm:px-[5vw] max-sm:py-3 max-sm:[scrollbar-width:none] sm:grid-cols-2 lg:mt-12 lg:grid-cols-4 max-sm:[&::-webkit-scrollbar]:hidden"
        >
          {featureCards.map((card, index) => (
            <article
              key={card.title}
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${featureCards.length}`}
              className="flex min-w-0 flex-col items-center rounded-[20px] border border-[#1F1F26] bg-pi-card px-5 pt-[26px] pb-6 text-center break-words transition duration-200 hover:-translate-y-1 hover:border-pi-accent max-sm:w-[90vw] max-sm:shrink-0 max-sm:snap-center max-sm:px-4 max-sm:pt-4 max-sm:pb-4"
            >
              <PhoneFrame
                className="w-full max-w-[190px] rounded-[34px] px-[9px] pt-[15px] pb-[11px]"
                notchClassName="top-2 w-[38px]"
              >
                <Image
                  src={card.image}
                  alt={card.imageAlt}
                  width={190}
                  height={380}
                  loading="lazy"
                  sizes="190px"
                  className="block aspect-[1/2] h-auto w-full rounded-[24px] object-cover"
                />
              </PhoneFrame>
              <div className="mt-[22px] text-[11px] font-bold tracking-[0.12em] text-pi-accent-soft uppercase max-sm:mt-3.5">
                {card.eyebrow}
              </div>
              <h3 className="mt-1.5 mb-0 text-lg font-semibold leading-snug text-pi-bright">
                {card.title}
              </h3>
              <p className="mt-1.5 mb-0 text-sm leading-normal text-pi-soft">
                {card.description}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-center gap-4 sm:hidden">
          <CarouselArrow
            label="Previous feature"
            rotation="rotate-90"
            disabled={activeIndex === 0}
            onClick={() => scrollToIndex(activeIndex - 1)}
          />
          <div className="flex items-center gap-2">
            {featureCards.map((card, index) => (
              <button
                key={card.title}
                type="button"
                aria-label={`Go to feature ${index + 1}`}
                aria-current={index === activeIndex}
                onClick={() => scrollToIndex(index)}
                className={`h-2 cursor-pointer rounded-full transition-all ${
                  index === activeIndex
                    ? "w-5 bg-pi-accent"
                    : "w-2 bg-pi-border-strong"
                }`}
              />
            ))}
          </div>
          <CarouselArrow
            label="Next feature"
            rotation="-rotate-90"
            disabled={activeIndex === featureCards.length - 1}
            onClick={() => scrollToIndex(activeIndex + 1)}
          />
        </div>
      </div>
    </section>
  );
}

function CarouselArrow({
  label,
  rotation,
  disabled,
  onClick,
}: {
  label: string;
  rotation: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-pi-border-strong bg-pi-elevated text-pi-bright transition-colors enabled:hover:border-pi-accent disabled:cursor-default disabled:opacity-40"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={rotation}
        aria-hidden="true"
      >
        <path d="M12 5v14M19 12l-7 7-7-7" />
      </svg>
    </button>
  );
}
